import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ChevronRight,
  Clock,
  Copy,
  Gift,
  Link as LinkIcon,
  Loader,
  Share2,
  Timer,
  Users,
  Calendar,
  SearchX,
} from "lucide-react";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Modal from "../../components/common/Modal";
import Toast from "../../components/common/Toast";
import { useUpdateWaitlist } from "../../customHooks/trip";
import { useGlobalState } from "../../GlobalStates";
import { Footer, StickyHeader, WhyChooseUs, FAQs } from "../../intro/components/landing-new";
import { getCampaignOverviewBySlug, getCampaignTripsBySlug } from "../../services/campaign";
import { useIsAuthenticated } from "../../utils/auth";
import { formatCurrency } from "../../utils/currency";
import dayjs from "dayjs";
import Markdown from "markdown-to-jsx";
import { convertUTCToLocalDateTimeString } from "../../utils/utils";
import { useTabletOrMobile, useIsDesktop } from "../../hooks/responsive";
import { AutoPlaySilentVideo } from "../../components/video";
import { useTranslation } from "react-i18next";
import { ImageResizable } from "../../components/elements/ImageResizeable";
import { dateRangeFormattingShortYear } from "../../utils/utils";
import { useUserLocale } from "../../customHooks/useUserLocale";
import { joinCampaign, hasJoinedCampaign } from "../../services/user";
import ReactConfetti from "react-confetti";
import instance from "../../axios";
import { useStripe } from "@stripe/react-stripe-js";
import { LuckyDrawModal } from "./LuckyDrawModal";
import EuropeCampaignPerks from "./EuropeCampaignPerks";
import AuroraStory from "./aurora/AuroraStory";
import AuroraPromises from "./aurora/AuroraPromises";
import AuroraDestinationSpotlight from "./aurora/AuroraDestinationSpotlight";
import AuroraDarkFooter from "./aurora/AuroraDarkFooter";
import AuroraReasonsGrid from "./aurora/AuroraReasonsGrid";
import AuroraFAQs from "./aurora/AuroraFAQs";
import { AURORA_ANCHORS, AURORA_THEME } from "./aurora/theme";
import {
  europeDestinationReasons,
  europeFaqs,
  auroraDestinationReasons,
  auroraFaqs,
} from "../../constants/destination";
import { getCampaignConfig } from "../../constants/campaign";
import { ownPageLinkProps } from "../../utils/appShell";

// Utility function to check if a campaign is in pre-launch state
const isPreLaunchCampaign = (campaign, checkLaunchMethod = false) => {
  if (!campaign) return false;

  // If we need to specifically check launch method
  if (checkLaunchMethod && (!campaign.launch_time || campaign.launch_method !== "early_access")) {
    return false;
  }

  if (campaign.launch_time) {
    return dayjs().isBefore(dayjs(campaign.launch_time));
  }

  if (campaign.publicSaleStartTime) {
    return dayjs().isBefore(dayjs(campaign.publicSaleStartTime));
  }

  return false;
};

// Helper function to generate login redirect URL
const getLoginRedirectUrl = () => {
  const originalPath = window.location.pathname + window.location.search;
  return `/login?reroute=${encodeURIComponent(originalPath)}`;
};

const WaitlistModal = ({
  waitlisters,
  totalCount,
  tripTitle,
  onClose,
  onShowToast,
  trip,
  onJoinWaitlist,
  open,
}) => {
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
  const { isAuthenticated } = useIsAuthenticated();
  const [user] = useGlobalState("user");
  const { t } = useTranslation();

  // Check if the current user has already joined the waitlist
  const hasJoinedWaitlist = useMemo(() => {
    if (!isAuthenticated || !user || !waitlisters) return false;
    return waitlisters.some((waitlister) => waitlister.id === user.id);
  }, [isAuthenticated, user, waitlisters]);

  const handleJoinWaitlist = async () => {
    if (!isAuthenticated) {
      window.location = getLoginRedirectUrl();
      return;
    }

    setIsJoiningWaitlist(true);

    try {
      // Use the actual API integration
      await onJoinWaitlist();
      onShowToast(t("campaign.waitlist.successfully_joined"), "success");
      onClose();
    } catch (error) {
      console.error("Error joining waitlist:", error);
      onShowToast(error.message || t("campaign.waitlist.failed_to_join"), "error");
    } finally {
      setIsJoiningWaitlist(false);
    }
  };

  const headerContent = (
    <div>
      <h2 className="tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-2xl tw-font-bold tw-text-transparent">
        {t("campaign.waitlist.for_title", { tripTitle })}
      </h2>
      <div className="tw-mt-2 tw-flex tw-items-center tw-space-x-2">
        {totalCount > 0 && (
          <>
            <Users className="tw-h-5 tw-w-5 tw-text-[deepskyblue]" />{" "}
            <span className="tw-text-gray-600">
              {totalCount}{" "}
              {totalCount === 1 ? t("campaign.waitlist.person") : t("campaign.waitlist.people")}{" "}
              {t("campaign.waitlist.waitlisted")}
            </span>
          </>
        )}
      </div>
    </div>
  );

  const footerContent = hasJoinedWaitlist ? (
    <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
      <div className="tw-flex tw-items-center tw-space-x-2 tw-text-emerald-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="tw-h-5 tw-w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <span className="tw-font-medium">{t("campaign.waitlist.youre_on_waitlist")}</span>
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClose}
        className="tw-rounded-xl tw-bg-gray-100 tw-px-4 tw-py-2 tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-200"
      >
        {t("common.close")}
      </motion.button>
    </div>
  ) : isAuthenticated ? (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleJoinWaitlist}
      disabled={isJoiningWaitlist}
      className="tw-flex tw-w-full tw-items-center tw-justify-center tw-space-x-2 tw-rounded-xl tw-bg-[deepskyblue] tw-py-3 tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-[#00a6e6] disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
    >
      {isJoiningWaitlist ? (
        <>
          <Loader className="tw-h-5 tw-w-5 tw-animate-spin" />
          <span>{t("campaign.waitlist.joining")}</span>
        </>
      ) : (
        <>
          <span>{t("campaign.waitlist.join_waitlist")}</span>
          <ChevronRight className="tw-h-5 tw-w-5" />
        </>
      )}
    </motion.button>
  ) : (
    <motion.a
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      href={getLoginRedirectUrl()}
      className="tw-flex tw-w-full tw-items-center tw-justify-center tw-space-x-2 tw-rounded-xl tw-bg-gray-200 tw-py-3 tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-300"
    >
      <span>{t("common.sign_in_to_waitlist")}</span>
      <ChevronRight className="tw-h-5 tw-w-5" />
    </motion.a>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      headerContent={headerContent}
      footerContent={footerContent}
    >
      <div className="tw-flex-1 tw-overflow-auto">
        {totalCount > 0 ? (
          <div className="tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent tw-max-h-[60vh] tw-overflow-y-auto tw-pr-2">
            <div className="tw-space-y-4">
              {waitlisters.map((waitlister, index) => (
                <motion.div
                  key={waitlister.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.03, 0.5) }}
                  className={`tw-flex tw-items-center tw-space-x-4 tw-rounded-xl tw-bg-gray-50 tw-p-4 tw-transition-colors hover:tw-bg-gray-100 ${
                    isAuthenticated && user && waitlister.id === user.id
                      ? "tw-border-emerald-200 tw-bg-emerald-50"
                      : ""
                  }`}
                >
                  <ImageResizable
                    size="xs"
                    src={waitlister.avatar_url}
                    alt={waitlister.name}
                    className="tw-h-12 tw-w-12 tw-rounded-full tw-border-2 tw-border-white tw-object-cover"
                    loading="lazy"
                  />
                  <div className="tw-flex-1">
                    <div className="tw-font-medium tw-text-gray-900">
                      {waitlister.name}
                      {isAuthenticated && user && waitlister.id === user.id && (
                        <span className="tw-ml-2 tw-rounded-full tw-bg-emerald-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-emerald-600">
                          {t("campaign.waitlist.you")}
                        </span>
                      )}
                    </div>
                    <div className="tw-text-sm tw-text-gray-500">
                      {t("campaign.waitlist.joined")}{" "}
                      {new Date(waitlister.joinedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="tw-rounded-full tw-bg-sky-50 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-[deepskyblue]">
                    #{index + 1}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6"
          >
            <div className="tw-relative tw-mb-8">
              <div className="tw-flex tw-h-32 tw-w-32 tw-items-center tw-justify-center tw-rounded-full tw-bg-gradient-to-br tw-from-sky-100 tw-to-blue-100">
                <Users className="tw-h-16 tw-w-16 tw-text-[deepskyblue]" />
              </div>
              <motion.div
                animate={{
                  y: [0, -5, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="tw-absolute -tw-right-2 -tw-top-2 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-400 tw-text-white tw-shadow-lg"
              >
                <span className="tw-text-lg tw-font-bold">1</span>
              </motion.div>
            </div>

            <h3 className="tw-mb-3 tw-text-center tw-text-xl tw-font-bold tw-text-gray-800">
              {t("campaign.waitlist.join_the_waitlist")}
            </h3>

            <p className="tw-mb-6 tw-max-w-md tw-text-center tw-text-gray-600">
              {t("campaign.waitlist.get_early_access", { tripTitle })}
            </p>

            <div className="tw-mb-6 tw-flex tw-flex-wrap tw-justify-center tw-gap-4">
              <div className="tw-flex tw-items-center tw-rounded-xl tw-bg-gray-50 tw-p-3">
                <div className="tw-mr-3 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-100">
                  <Timer className="tw-h-4 tw-w-4 tw-text-emerald-600" />
                </div>
                <span className="tw-text-sm tw-text-gray-700">
                  {t("campaign.waitlist.early_bird_deal")}
                </span>
              </div>

              <div className="tw-flex tw-items-center tw-rounded-xl tw-bg-gray-50 tw-p-3">
                <div className="tw-mr-3 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-100">
                  <AlertTriangle className="tw-h-4 tw-w-4 tw-text-amber-600" />
                </div>
                <span className="tw-text-sm tw-text-gray-700">
                  {t("campaign.waitlist.limited_spots")}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </Modal>
  );
};

const WaitlistersPreview = ({ waitlisters, totalCount, tripTitle, onShowAll }) => {
  const { t } = useTranslation();
  const displayCount = 5;
  const displayedWaitlisters = waitlisters.slice(0, displayCount);
  const [user] = useGlobalState("user");
  const { isAuthenticated } = useIsAuthenticated();

  // Check if the current user has already joined the waitlist
  const hasJoinedWaitlist = useMemo(() => {
    if (!isAuthenticated || !user || !waitlisters) return false;
    return waitlisters.some((waitlister) => waitlister.id === user.id);
  }, [isAuthenticated, user, waitlisters]);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onClick={onShowAll}
      className="tw-flex tw-cursor-pointer tw-items-center tw-space-x-3"
    >
      <div className="tw-flex -tw-space-x-2">
        {displayedWaitlisters.map((waitlister, index) => (
          <motion.div
            key={waitlister.id}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2, delay: index * 0.1, ease: "easeOut" }}
            className={`tw-group tw-relative tw-h-8 tw-w-8 tw-overflow-hidden tw-rounded-full tw-border-2 ${
              isAuthenticated && user && waitlister.id === user.id
                ? "tw-border-emerald-300 tw-ring-2 tw-ring-emerald-200"
                : "tw-border-white"
            }`}
          >
            <ImageResizable
              size="xs"
              src={waitlister.avatar_url}
              alt={waitlister.name}
              className="tw-h-full tw-w-full tw-object-cover"
            />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileHover={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="tw-group-hover:tw-visible tw-invisible tw-absolute tw-bottom-full tw-left-1/2 tw-z-10 -tw-translate-x-1/2 -tw-translate-y-2 tw-transform tw-whitespace-nowrap tw-rounded-lg tw-bg-black/75 tw-px-2 tw-py-1 tw-text-xs tw-text-white tw-backdrop-blur-sm"
            >
              {waitlister.name}
              {isAuthenticated && user && waitlister.id === user.id && (
                <span className="tw-ml-1 tw-text-emerald-300">(You)</span>
              )}
              <div className="tw-text-[10px] tw-text-gray-300">
                {t("campaign.waitlist.joined")} {new Date(waitlister.joinedAt).toLocaleDateString()}
              </div>
            </motion.div>
          </motion.div>
        ))}
        {totalCount > displayCount && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2, delay: displayCount * 0.1 }}
            className="tw-group tw-relative tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-white tw-bg-gray-100"
          >
            <span className="tw-text-xs tw-font-medium tw-text-gray-600">
              +{totalCount - displayCount}
            </span>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileHover={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="tw-group-hover:tw-visible tw-invisible tw-absolute tw-bottom-full tw-left-1/2 tw-z-10 -tw-translate-x-1/2 -tw-translate-y-2 tw-transform tw-whitespace-nowrap tw-rounded-lg tw-bg-black/75 tw-px-2 tw-py-1 tw-text-xs tw-text-white tw-backdrop-blur-sm"
            >
              {totalCount - displayCount} {t("campaign.waitlist.more_waitlisters")}
            </motion.div>
          </motion.div>
        )}
      </div>
      <div className="tw-flex tw-flex-col">
        <span className="tw-text-sm tw-font-medium tw-text-gray-900">
          {totalCount} {t("campaign.waitlist.waitlisted")}
          {hasJoinedWaitlist && (
            <span className="tw-ml-1 tw-text-xs tw-font-medium tw-text-emerald-600">
              {t("campaign.waitlist.including_you")}
            </span>
          )}
        </span>
      </div>
    </motion.div>
  );
};

const TripCard = React.memo(
  ({
    trip,
    onShowToast,
    onJoinWaitlist,
    cardHeight,
    onShowWaitlistModal,
    campaign,
    promotion,
    projectView = false,
  }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const { isAuthenticated } = useIsAuthenticated();
    const [user] = useGlobalState("user");
    const descriptionRef = useRef(null);
    const [isDescriptionLong, setIsDescriptionLong] = useState(false);

    // Aurora dark variant — applies a deep-space card surface + aurora-green accents.
    // Gated on slug so EU and other campaigns keep their existing white card chrome.
    const isAuroraDark = campaign?.slug === "aurora";
    const auroraAccent = "#00FFCC";
    const auroraSurface = "#141a3a";
    const auroraElevated = "#1c2350";
    const auroraTextSecondary = "rgba(244, 246, 255, 0.72)";
    const auroraTextPrimary = "#F4F6FF";

    // Check if the current user has already joined the waitlist
    const hasJoinedWaitlist = useMemo(() => {
      if (!isAuthenticated || !user || !trip.waitlisters) return false;
      return trip.waitlisters.some((waitlister) => waitlister.id === user.id);
    }, [isAuthenticated, user, trip.waitlisters]);

    // Check if the campaign is still in pre-launch state
    // Aurora is a social-media giveaway campaign — the LP never exposes waitlist/lucky-draw UI.
    const isPreLaunch = useMemo(
      () => campaign?.slug !== "aurora" && isPreLaunchCampaign(campaign),
      [campaign]
    );

    // Check if description is long on mount
    useEffect(() => {
      if (descriptionRef.current) {
        const isLong = descriptionRef.current.scrollHeight > 80; // 4 lines of text approximately
        setIsDescriptionLong(isLong);
      }
    }, [trip.description]);

    const handleJoinWaitlist = async () => {
      if (!isAuthenticated) {
        window.location = getLoginRedirectUrl();
        return;
      }

      setIsJoiningWaitlist(true);

      try {
        // Use the actual API integration
        await onJoinWaitlist();
      } catch (error) {
        console.error("Error in handleJoinWaitlist:", error);
      } finally {
        setIsJoiningWaitlist(false);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`tw-group tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-shadow-sm tw-transition-all ${
          isAuroraDark ? "" : "tw-bg-white"
        }`}
        style={
          isAuroraDark
            ? {
                height: cardHeight,
                backgroundColor: auroraSurface,
                border: "1px solid rgba(0, 255, 204, 0.12)",
              }
            : { height: cardHeight }
        }
      >
        <div className="tw-relative">
          <ImageResizable
            size="oxl"
            src={trip.image}
            alt={trip.title}
            className="tw-h-64 tw-w-full tw-object-cover"
          />
          <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/70 tw-via-black/20 tw-to-transparent"></div>

          {/* Display user images overlay if available */}
          {trip.three_user_images && trip.three_user_images.length > 0 && (
            <div className="tw-absolute tw-left-4 tw-top-4 tw-flex -tw-space-x-2">
              {trip.three_user_images.slice(0, 3).map((img, idx) => (
                <div
                  key={idx}
                  className="tw-h-8 tw-w-8 tw-overflow-hidden tw-rounded-full tw-border-2 tw-border-white"
                >
                  <ImageResizable
                    size="xs"
                    src={img}
                    alt="User"
                    className="tw-h-full tw-w-full tw-object-cover"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="tw-absolute tw-right-4 tw-top-4">
            <div
              className="tw-flex tw-items-center tw-space-x-1 tw-rounded-xl tw-px-3 tw-py-1.5 tw-backdrop-blur-sm"
              style={
                isAuroraDark
                  ? { backgroundColor: "rgba(11, 16, 38, 0.75)" }
                  : { backgroundColor: "rgba(255, 255, 255, 0.9)" }
              }
            >
              <Clock
                className="tw-h-4 tw-w-4"
                style={{ color: isAuroraDark ? auroraAccent : "deepskyblue" }}
              />
              <span
                className="tw-text-sm tw-font-medium"
                style={isAuroraDark ? { color: auroraTextPrimary } : undefined}
              >
                {t("trip_details_duration", {
                  days: trip.num_nights + 1,
                  nights: trip.num_nights,
                })}
              </span>
            </div>
          </div>
          <div className="tw-absolute tw-bottom-4 tw-left-4 tw-right-4">
            <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-2">
              <div>
                <h3 className="tw-mb-1 tw-line-clamp-2 tw-text-xl tw-font-bold tw-text-white">
                  {trip.title}
                </h3>
                <div className="tw-mb-2 tw-flex tw-items-center tw-space-x-2"></div>
              </div>
              <div className="tw-flex tw-min-w-[100px] tw-flex-col tw-items-end">
                {promotion?.discountedTripIdToPromoObject[trip?.id]?.special_discount_percentage >
                0 ? (
                  <div className="tw-flex tw-flex-col tw-items-end">
                    <span className="tw-text-xl tw-font-medium tw-text-white/70 tw-line-through">
                      {formatCurrency(trip.price)}
                    </span>
                    <span className="tw-whitespace-nowrap tw-text-xl tw-font-bold tw-text-[lightgreen] tw-text-white">
                      {formatCurrency(
                        Math.ceil(
                          (trip.price *
                            (100 -
                              promotion?.discountedTripIdToPromoObject[trip?.id]
                                ?.special_discount_percentage)) /
                            100
                        )
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="tw-whitespace-nowrap tw-text-xl tw-font-bold tw-text-white">
                    {formatCurrency(trip.price)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden tw-p-4">
          <div className="tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent tw-flex-1 tw-overflow-y-auto tw-pr-2">
            {/* Display local host if available */}
            {/* {trip.local_host && (
              <div className="tw-flex tw-items-center tw-mb-3">
                <div className="tw-relative tw-mr-3">
                  <img
                    src={
                      trip.local_host.image ||
                      `https://i.pravatar.cc/150?img=${Math.floor(
                        Math.random() * 70
                      )}`
                    }
                    alt={trip.local_host.first_name}
                    className="tw-w-8 tw-h-8 tw-rounded-full tw-object-cover"
                  />
                  <div className="tw-absolute -tw-bottom-1 -tw-right-1 tw-w-4 tw-h-4 tw-bg-emerald-500 tw-rounded-full tw-border-2 tw-border-white"></div>
                </div>
                <div>
                  <p className="tw-text-xs tw-text-gray-500">
                    {t("campaign.trip_card.our_partner")}
                  </p>
                  <p className="tw-text-sm tw-font-medium">
                    {trip.local_host.first_name ||
                      t("campaign.trip_card.local_host")}
                  </p>
                </div>
              </div>
            )} */}

            <div className="tw-relative tw-mb-4">
              <div className={`tw-relative ${expanded ? "tw-pb-8" : ""}`}>
                <div
                  ref={descriptionRef}
                  className={`tw-whitespace-pre-line tw-text-sm ${
                    isAuroraDark ? "" : "tw-text-gray-600"
                  } ${!expanded && isDescriptionLong ? "tw-line-clamp-3" : ""}`}
                  style={isAuroraDark ? { color: auroraTextSecondary } : undefined}
                >
                  {(trip.summary || "").split("\n").map(
                    (paragraph, index) =>
                      paragraph.trim() && (
                        <p key={index} style={{ marginTop: index > 0 ? "8px" : "0" }}>
                          <Markdown
                            options={{
                              overrides: {
                                a: {
                                  component: ({ children }) => (
                                    <span
                                      style={{
                                        fontWeight: "bold",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      {children}
                                    </span>
                                  ),
                                },
                              },
                            }}
                          >
                            {paragraph}
                          </Markdown>
                        </p>
                      )
                  )}
                </div>
                {isDescriptionLong && !expanded && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setExpanded(!expanded)}
                    className={`tw-mt-1 tw-text-xs tw-font-medium ${
                      isAuroraDark ? "" : "tw-text-[deepskyblue]"
                    } ${
                      expanded
                        ? "tw-absolute tw-bottom-0 tw-right-0 tw-rounded-md tw-px-2 tw-py-1 tw-shadow-sm " +
                          (isAuroraDark ? "" : "tw-bg-white")
                        : ""
                    }`}
                    style={isAuroraDark ? { color: auroraAccent } : undefined}
                  >
                    {expanded
                      ? t("campaign.trip_card.read_less")
                      : t("campaign.trip_card.read_more")}
                  </motion.button>
                )}
              </div>
            </div>

            {/* <div className="tw-mb-4">
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {trip.highlights &&
                  trip.highlights.map((highlight, index) => (
                    <span
                      key={index}
                      className="tw-rounded-full tw-bg-sky-50 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-[deepskyblue]"
                    >
                      {highlight}
                    </span>
                  ))}
              </div>
            </div> */}

            {/* Trip Date Section */}
            <div
              className={`tw-mb-2 tw-rounded-xl tw-p-3 ${
                isAuroraDark ? "" : "tw-bg-gray-50"
              }`}
              style={isAuroraDark ? { backgroundColor: auroraElevated } : undefined}
            >
              <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
                <span
                  className={`tw-text-sm tw-font-medium ${
                    isAuroraDark ? "" : "tw-text-gray-700"
                  }`}
                  style={isAuroraDark ? { color: auroraTextPrimary } : undefined}
                >
                  {projectView
                    ? t("campaign.trip_card.trip_dates_available")
                    : t("campaign.trip_card.trip_date")}
                </span>
              </div>
              <div className="tw-space-y-2">
                {projectView ? (
                  trip?.all_dates?.map((date, index) => {
                    return (
                      <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                        <div className="tw-flex tw-items-center tw-space-x-2" key={index}>
                          <div
                            className="tw-h-1.5 tw-w-1.5 tw-rounded-full"
                            style={{
                              backgroundColor: isAuroraDark ? auroraAccent : "deepskyblue",
                            }}
                          ></div>
                          <span
                            className={isAuroraDark ? "" : "tw-text-gray-600"}
                            style={isAuroraDark ? { color: auroraTextSecondary } : undefined}
                          >
                            {date}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <div
                        className="tw-h-1.5 tw-w-1.5 tw-rounded-full"
                        style={{
                          backgroundColor: isAuroraDark ? auroraAccent : "deepskyblue",
                        }}
                      ></div>
                      <span
                        className={isAuroraDark ? "" : "tw-text-gray-600"}
                        style={isAuroraDark ? { color: auroraTextSecondary } : undefined}
                      >
                        {dateRangeFormattingShortYear(trip.startDate, trip.endDate)}
                      </span>
                    </div>
                  </div>
                )}

                {/* <div className="tw-flex tw-items-center tw-space-x-2">
                    {trip.soldOut ? (
                      <span className="tw-text-xs tw-bg-red-100 tw-text-red-600 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {t("campaign.trip_card.sold_out")}
                      </span>
                    ) : trip.spotsLeft < 5 ? (
                      <span className="tw-text-xs tw-bg-amber-100 tw-text-amber-600 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {trip.spotsLeft} {t("campaign.trip_card.left")}
                      </span>
                    ) : (
                      <span className="tw-text-xs tw-bg-emerald-100 tw-text-emerald-600 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {t("campaign.trip_card.available")}
                      </span>
                    )}
                  </div> */}
              </div>
            </div>
          </div>

          <div
            className={`tw-mt-2 tw-border-t tw-pt-4 ${
              isAuroraDark ? "" : "tw-border-gray-100"
            }`}
            style={
              isAuroraDark ? { borderColor: "rgba(0, 255, 204, 0.1)" } : undefined
            }
          >
            {!projectView && isPreLaunch && (
              <div className="tw-mb-4">
                <WaitlistersPreview
                  waitlisters={trip.waitlisters}
                  totalCount={trip.waitlistCount}
                  tripTitle={trip.title}
                  onShowAll={() => onShowWaitlistModal(trip)}
                />
              </div>
            )}

            <div className="tw-flex tw-gap-3">
              <motion.a
                href={`${window.location.origin}/trips/${trip.project_id}`}
                {...ownPageLinkProps(navigate, `/trips/${trip.project_id}`)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`tw-flex tw-items-center tw-justify-center tw-space-x-2 tw-rounded-xl tw-py-3 tw-font-bold tw-transition-all ${
                  isPreLaunch ? "tw-flex-1" : "tw-w-full"
                } ${
                  isAuroraDark
                    ? ""
                    : "tw-bg-[#00bfff] tw-text-white hover:tw-bg-[#00a6e6]"
                }`}
                style={
                  isAuroraDark
                    ? {
                        // Glassmorphic CTA — translucent dark surface with an aurora-green
                        // hairline + green text + soft outer/inner glow. Premium dark UI
                        // pattern that reads as elegant rather than a loud flat mint button.
                        background:
                          "linear-gradient(135deg, rgba(20,26,58,0.85) 0%, rgba(28,35,80,0.85) 100%)",
                        color: auroraAccent,
                        border: "1px solid rgba(0, 255, 204, 0.55)",
                        boxShadow:
                          "0 0 18px rgba(0, 255, 204, 0.18), inset 0 0 14px rgba(0, 255, 204, 0.1)",
                        backdropFilter: "blur(8px)",
                      }
                    : undefined
                }
              >
                <span>{t("campaign.trip_card.explore")}</span>
                <ChevronRight className="tw-h-5 tw-w-5" />
              </motion.a>

              {!projectView && isPreLaunch && (
                <>
                  {hasJoinedWaitlist ? (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-space-x-2 tw-rounded-xl tw-bg-emerald-100 tw-py-3 tw-font-medium tw-text-emerald-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="tw-h-5 tw-w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      <span>{t("campaign.trip_card.waitlisted")}</span>
                    </motion.div>
                  ) : isAuthenticated ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleJoinWaitlist}
                      disabled={isJoiningWaitlist}
                      className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-space-x-2 tw-rounded-xl tw-bg-[deepskyblue] tw-py-3 tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-[#00a6e6] disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                    >
                      {isJoiningWaitlist ? (
                        <>
                          <Loader className="tw-h-5 tw-w-5 tw-animate-spin" />
                          <span>{t("campaign.waitlist.joining")}</span>
                        </>
                      ) : (
                        <>
                          <span>{t("campaign.waitlist.join_waitlist")}</span>
                          <ChevronRight className="tw-h-5 tw-w-5" />
                        </>
                      )}
                    </motion.button>
                  ) : (
                    <motion.a
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      href={getLoginRedirectUrl()}
                      className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-space-x-2 tw-rounded-xl tw-bg-gray-200 tw-py-3 tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-300"
                    >
                      <span>{t("common.sign_in_to_waitlist")}</span>
                      <ChevronRight className="tw-h-5 tw-w-5" />
                    </motion.a>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }
);

const SaleTimer = ({ campaign }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState("");
  const [saleStatus, setSaleStatus] = useState("upcoming"); // upcoming, early, public

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = dayjs();

      // Handle campaigns without launch_time or early access
      if (!campaign.launch_time || campaign.launch_method !== "early_access") {
        // Fall back to publicSaleStartTime if available
        if (campaign.publicSaleStartTime) {
          const publicSale = dayjs(campaign.publicSaleStartTime);
          if (now.isAfter(publicSale)) {
            setSaleStatus("public");
            return t("campaign.timer.public_sale_live");
          } else {
            setSaleStatus("upcoming");
            const diff = publicSale.diff(now);
            const minutes = Math.floor((diff / 1000 / 60) % 60);
            const hours = Math.floor((diff / 1000 / 60 / 60) % 24);
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));

            if (days > 0) {
              return t("campaign.timer.sale_starts_in", { days, hours });
            }
            return t("campaign.timer.sale_starts_in_hours", { hours, minutes });
          }
        }
        setSaleStatus("public");
        return t("campaign.timer.sale_live");
      }

      const launchTime = dayjs(campaign.launch_time);

      // Calculate early access time - 2 hours before launch
      const earlyAccessTime = launchTime.subtract(2, "hours");

      if (now.isAfter(launchTime) || now.isSame(launchTime)) {
        setSaleStatus("public");
        return t("campaign.timer.public_sale_live");
      } else if (now.isAfter(earlyAccessTime) || now.isSame(earlyAccessTime)) {
        setSaleStatus("early");
        const diff = launchTime.diff(now);
        const minutes = Math.floor((diff / 1000 / 60) % 60);
        const hours = Math.floor((diff / 1000 / 60 / 60) % 24);
        return t("campaign.timer.public_sale_in", { hours, minutes });
      } else {
        setSaleStatus("upcoming");
        const diff = earlyAccessTime.diff(now);
        const minutes = Math.floor((diff / 1000 / 60) % 60);
        const hours = Math.floor((diff / 1000 / 60 / 60) % 24);
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        // If more than 24 hours away, show days
        if (days > 0) {
          return t("campaign.timer.early_access_in", { days, hours });
        }
        return t("campaign.timer.early_access_in_hours", { hours, minutes });
      }
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 60000);
    return () => clearInterval(timer);
  }, [campaign, t]);

  // Aurora gets a glassmorphic pill (translucent dark + green hairline + green glow)
  // instead of the flat emerald/blue/gray pills used elsewhere — matches the rest of
  // the dark theme.
  const isAurora = campaign?.slug === "aurora";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className={`tw-fixed tw-bottom-4 tw-left-1/2 tw-z-50 tw-flex tw-w-[300px] -tw-translate-x-1/2 tw-items-center tw-justify-center tw-space-x-3 tw-rounded-full tw-px-8 tw-py-3 tw-shadow-lg ${
        isAurora
          ? "tw-backdrop-blur-md"
          : saleStatus === "public"
            ? "tw-bg-emerald-500"
            : saleStatus === "early"
              ? "tw-bg-[deepskyblue]"
              : "tw-bg-gray-900"
      }`}
      style={
        isAurora
          ? {
              background:
                "linear-gradient(135deg, rgba(20,26,58,0.88) 0%, rgba(28,35,80,0.88) 100%)",
              border: "1px solid rgba(0, 255, 204, 0.45)",
              boxShadow:
                "0 0 22px rgba(0, 255, 204, 0.2), inset 0 0 14px rgba(0, 255, 204, 0.08)",
            }
          : undefined
      }
    >
      <Timer
        className="tw-h-5 tw-w-5"
        style={{ color: isAurora ? "#00FFCC" : "white" }}
      />
      <span
        className="tw-font-medium"
        style={{ color: isAurora ? "#00FFCC" : "white" }}
      >
        {timeLeft}
      </span>
    </motion.div>
  );
};

const CampaignExplainer = ({ campaign, community }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="tw-relative tw-mb-12 tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-sky-500 tw-to-blue-600 tw-p-10 tw-shadow-xl"
    >
      {/* Background pattern */}
      <div className="tw-bg-grid-white/[0.2] tw-absolute tw-inset-0 tw-opacity-20" />

      <div className="tw-relative tw-mb-12 tw-flex tw-flex-col tw-items-center tw-text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring" }}
          className="tw-mb-6 tw-rounded-full tw-bg-white tw-px-6 tw-py-2 tw-text-sm tw-font-semibold tw-text-[deepskyblue] tw-shadow-lg"
        >
          {t("campaign.explainer.how_it_works")}
        </motion.div>
        <h2 className="tw-mb-4 tw-text-3xl tw-font-bold tw-text-white">
          {t("campaign.explainer.early_access")}
        </h2>
        <p className="tw-text-m md:tw-text-m tw-mx-auto tw-mb-6 tw-max-w-3xl tw-text-white/90">
          {t("campaign.explainer.referral_system")}
        </p>
      </div>

      <div className="tw-grid tw-gap-8 md:tw-grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="tw-group tw-flex tw-flex-col tw-items-center tw-rounded-2xl tw-bg-white/10 tw-p-6 tw-text-center tw-backdrop-blur-sm tw-transition-all hover:tw-bg-white/20"
        >
          <div className="tw-group-hover:tw-scale-110 tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-white tw-shadow-lg tw-transition-transform">
            <Users className="tw-h-8 tw-w-8 tw-text-[deepskyblue]" />
          </div>
          <h3 className="tw-mb-3 tw-text-lg tw-font-semibold tw-text-white">
            {t("campaign.explainer.join_waitlist")}
          </h3>
          <p className="tw-text-sm tw-text-sky-100">{t("campaign.explainer.pick_favorites")}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="tw-group tw-flex tw-flex-col tw-items-center tw-rounded-2xl tw-bg-white/10 tw-p-6 tw-text-center tw-backdrop-blur-sm tw-transition-all hover:tw-bg-white/20"
        >
          <div className="tw-group-hover:tw-scale-110 tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-white tw-shadow-lg tw-transition-transform">
            <Share2 className="tw-h-8 tw-w-8 tw-text-[deepskyblue]" />
          </div>
          <h3 className="tw-mb-3 tw-text-lg tw-font-semibold tw-text-white">
            {t("campaign.explainer.refer_friends")}
          </h3>
          <p className="tw-text-sm tw-text-sky-100">{t("campaign.explainer.share_link")}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="tw-group tw-flex tw-flex-col tw-items-center tw-rounded-2xl tw-bg-white/10 tw-p-6 tw-text-center tw-backdrop-blur-sm tw-transition-all hover:tw-bg-white/20"
        >
          <div className="tw-group-hover:tw-scale-110 tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-white tw-shadow-lg tw-transition-transform">
            <Timer className="tw-h-8 tw-w-8 tw-text-[deepskyblue]" />
          </div>
          <h3 className="tw-mb-3 tw-text-lg tw-font-semibold tw-text-white">
            {t("campaign.explainer.one_hour_headstart")}
          </h3>
          <p className="tw-text-sm tw-text-sky-100">{t("campaign.explainer.secret_link")}</p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="tw-mt-12 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-bg-white/10 tw-p-4 tw-backdrop-blur-sm"
      >
        <AlertTriangle className="tw-h-5 tw-w-5 tw-text-amber-300" />
        <p className="tw-text-sm tw-text-white">
          <span className="tw-font-medium">{t("campaign.explainer.spots_limited")}</span>
        </p>
      </motion.div>
    </motion.div>
  );
};

const CampaignPromoCodes = ({ campaign, user_id, setOpenLuckyDrawModal, setLuckyDrawLoading, campaignSettings }) => {
  const { t } = useTranslation();
  const [joinedCampaign, setJoinedCampaign] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  // Handle window resize
  const handleResize = useCallback(() => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (campaign?.id && user_id) {
      hasJoinedCampaign(campaign.id, user_id).then((res) => {
        setJoinedCampaign(res.data.joined);
      });
    }
  }, [campaign, user_id]);

  const handleJoinCampaign = async () => {
    try {
      if (!joinedCampaign && user_id) {
        setTimeout(() => setShowConfetti(false), 10000);
      }

      setOpenLuckyDrawModal(true);
      
      // Wait for the API call to complete before stopping loading
      await joinCampaign(campaign.id);

      setJoinedCampaign(true);
      setLuckyDrawLoading(false); // Stop loading after API completes
    } catch (error) {
      console.error("Error in handleJoinCampaign:", error);
      setLuckyDrawLoading(false); // Stop loading on error
    }
  };

  // Mock data for promo codes - in a real app, this would come from props or an API
  const promoCodes = [
    { id: 1, discount: "$888", ticketsLeft: 0 },
    { id: 2, discount: "$288", ticketsLeft: 1 },
    { id: 3, discount: "$188", ticketsLeft: 4 },
    { id: 4, discount: "$88", ticketsLeft: 89 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="tw-relative tw-mb-12 tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-purple-500 tw-to-indigo-600 tw-p-8 tw-shadow-xl"
    >
      {/* Confetti Effect */}
      {showConfetti && (
        <ReactConfetti
          width={dimensions.width}
          height={dimensions.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.2}
        />
      )}
      {/* Background pattern */}
      <div className="tw-bg-grid-white/[0.1] tw-absolute tw-inset-0 tw-opacity-20" />

      <div className="tw-relative tw-mb-8 tw-flex tw-flex-col tw-items-center tw-text-center">
        <h2 className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-white md:tw-text-3xl">
          Winter promo codes are up for grabs!
        </h2>
        <p className="tw-max-w-2xl tw-text-white/80">
          Our biggest discounts for our winter adventures!
        </p>
      </div>

      <div className="tw-relative tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-4">
        {promoCodes.map((promo, index) => (
          <motion.div
            key={promo.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
            className="tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-border-2 tw-border-white/20 tw-bg-white/10 tw-p-6 tw-backdrop-blur-sm"
          >
            {/* Ticket notch */}
            <div className="tw-absolute tw-left-0 tw-top-0 tw-flex tw-h-4 tw-w-full tw-justify-between tw-px-2">
              <div className="tw--ml-2 tw--mt-2 tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-br tw-from-purple-500 tw-to-indigo-600" />
              <div className="tw--mr-2 tw--mt-2 tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-br tw-from-purple-500 tw-to-indigo-600" />
            </div>

            <div className="tw-mt-2 tw-text-center">
              <span className="tw-text-3xl tw-font-bold tw-text-white">{promo.discount}</span>
              <p className="tw-mt-1 tw-text-sm tw-text-white/80">OFF</p>
            </div>

            <div className="tw-mt-4 tw-w-full tw-border-t tw-border-white/20 tw-pt-4 tw-text-center">
              <p className="tw-mb-1 tw-text-xs tw-text-white/60">Tickets left</p>
              {promo.ticketsLeft ? (
                <span className="tw-font-medium tw-text-white">{promo.ticketsLeft}</span>
              ) : (
                <span className="tw-font-medium tw-text-white">Claimed</span>
              )}
            </div>

            {/* Ticket bottom notch */}
            <div className="tw-absolute tw-bottom-0 tw-left-0 tw-flex tw-h-4 tw-w-full tw-justify-between tw-px-2">
              <div className="tw--mb-2 tw--ml-2 tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-br tw-from-purple-500 tw-to-indigo-600" />
              <div className="tw--mb-2 tw--mr-2 tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-br tw-from-purple-500 tw-to-indigo-600" />
            </div>
          </motion.div>
        ))}
      </div>

      {/* <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="tw-mt-6 tw-flex tw-items-center tw-justify-center"
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="tw-flex tw-h-12 tw-w-[10rem] tw-translate-y-1 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-purple-700 tw-p-3 tw-shadow-md tw-transition-all hover:tw-translate-y-0"
        >
          <p className="tw-text-s tw-font-bold tw-text-white">NORAGRETS77</p>
        </motion.button>
      </motion.div>
      <p className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-white">
        Use the promo code NORAGRETS77 at checkout to enjoy $77 off!
      </p> */}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="tw-mt-6 tw-flex tw-items-center tw-justify-center"
      >
        <motion.button
          onClick={handleJoinCampaign}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="tw-flex tw-h-12 tw-w-[12rem] tw-translate-y-1 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-purple-700 tw-p-3 tw-shadow-md tw-transition-all hover:tw-translate-y-0"
        >
          <p className="tw-text-s tw-font-bold tw-text-white">
            {joinedCampaign ? (
              <span className="tw-font-medium">You're in!</span>
            ) : (
              <span className="tw-font-bold">{campaignSettings.buttonText}</span>
            )}
          </p>
        </motion.button>
      </motion.div>
      {joinedCampaign ? (
        <p className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-white">
          Check your Sotravel app ("Chats" -&gt; "Sotravel Notifications") for the confirmation
          message!
        </p>
      ) : null}
    </motion.div>
  );
};

const BundlePass = ({ campaign, user_id, campaignSettings }) => {
  const stripe = useStripe();

  const { t } = useTranslation();
  const [joinedCampaign, setJoinedCampaign] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  // Handle window resize
  const handleResize = useCallback(() => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (campaign?.id && user_id) {
      hasJoinedCampaign(campaign.id, user_id).then((res) => {
        setJoinedCampaign(res.data.joined);
      });
    }
  }, [campaign, user_id]);

  const handlePurchasePass = async () => {
    const response = await instance.post("/v1/stripe/createCustomCheckoutSession", {
      uploaded_id: 1,
      friend_name: "larry",
    });

    const { url } = await response.data;

    stripe.redirectToCheckout({ sessionId: url });
    window.location.href = url;
  };

  // Mock data for promo codes - in a real app, this would come from props or an API
  const promoCodes = [
    { id: 1, discount: "$777", ticketsLeft: 0 },
    { id: 2, discount: "$247", ticketsLeft: 0 },
    { id: 3, discount: "$147", ticketsLeft: 0 },
    { id: 4, discount: "$77", ticketsLeft: 48 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="tw-relative tw-mb-12 tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-purple-500 tw-to-indigo-600 tw-p-8 tw-shadow-xl"
    >
      {/* Confetti Effect */}
      {showConfetti && (
        <ReactConfetti
          width={dimensions.width}
          height={dimensions.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.2}
        />
      )}
      {/* Background pattern */}
      <div className="tw-bg-grid-white/[0.1] tw-absolute tw-inset-0 tw-opacity-20" />

      <div className="tw-relative tw-mb-8 tw-flex tw-flex-col tw-items-center tw-text-center">
        <h2 className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-white md:tw-text-3xl">
          $27,777 in promo codes are up for grabs!
        </h2>
        <p className="tw-max-w-2xl tw-text-white/80">
          Our biggest discounts for our biggest adventures!
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="tw-mt-6 tw-flex tw-items-center tw-justify-center"
      >
        <motion.button
          onClick={handlePurchasePass}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="tw-flex tw-h-12 tw-w-[10rem] tw-translate-y-1 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-purple-700 tw-p-3 tw-shadow-md tw-transition-all hover:tw-translate-y-0"
        >
          <p className="tw-text-s tw-font-bold tw-text-white">NORAGRETS77</p>
        </motion.button>
      </motion.div>
      <p className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-white">
        Use the promo code NORAGRETS77 at checkout to enjoy $77 off!
      </p>
    </motion.div>
  );
};

const ScrollIndicator = () => {
  const { t } = useTranslation();
  const scrollToTrips = () => {
    document.getElementById("available-trips").scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Add state to track if we should show the indicator
  const [showIndicator, setShowIndicator] = useState(true);

  // Add effect to handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const tripsSection = document.getElementById("available-trips");
      if (tripsSection) {
        const tripsSectionPosition = tripsSection.getBoundingClientRect().top;
        // Show indicator if trips section is below viewport
        setShowIndicator(tripsSectionPosition > window.innerHeight);
      }
    };

    // Initial check
    handleScroll();

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: showIndicator ? 1 : 0 }}
      transition={{ delay: 0.6 }}
      onClick={scrollToTrips}
      className={`tw-fixed tw-bottom-20 tw-left-1/2 tw-z-40 tw-flex -tw-translate-x-1/2 tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-transition-transform hover:tw-scale-105 ${
        !showIndicator ? "tw-pointer-events-none" : ""
      }`}
    >
      <motion.p
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="tw-rounded-full tw-bg-white/80 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-500 tw-shadow-sm tw-backdrop-blur-sm"
      >
        {t("campaign.trips.scroll_to_see_trips")}
      </motion.p>
      <motion.div
        animate={{ y: [0, 5, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="tw-rounded-full tw-bg-white/80 tw-p-2 tw-shadow-sm tw-backdrop-blur-sm"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="tw-h-6 tw-w-6 tw-text-[deepskyblue]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </motion.div>
    </motion.div>
  );
};

// Empty state component for when no trips are available
const EmptyTripsState = ({ campaign }) => {
  const { t } = useTranslation();
  const isPreLaunch = useMemo(() => isPreLaunchCampaign(campaign), [campaign]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-2xl tw-bg-gray-50 tw-px-4 tw-py-16 tw-text-center tw-shadow-sm tw-transition-all"
    >
      <div className="tw-relative tw-mb-6">
        <div className="tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100">
          <SearchX className="tw-h-12 tw-w-12 tw-text-gray-400" />
        </div>
        {isPreLaunch && (
          <motion.div
            animate={{
              y: [0, -5, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="tw-absolute -tw-right-2 -tw-top-2 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-[deepskyblue] tw-text-white tw-shadow-lg"
          >
            <Calendar className="tw-h-5 tw-w-5" />
          </motion.div>
        )}
      </div>

      <h3 className="tw-mb-3 tw-text-xl tw-font-bold tw-text-gray-800">
        {isPreLaunch
          ? t("campaign.trips.no_trips_yet") || "No trips available yet"
          : t("campaign.trips.no_trips_available") || "No trips available"}
      </h3>

      <p className="tw-mb-6 tw-max-w-md tw-text-gray-600">
        {isPreLaunch
          ? t("campaign.trips.check_back_later") ||
            "Check back later for exciting new trips coming to this campaign."
          : t("campaign.trips.all_trips_sold_out") ||
            "It looks like all trips for this campaign have sold out or are no longer available."}
      </p>

      <motion.a
        href="/"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="tw-flex tw-items-center tw-justify-center tw-space-x-2 tw-rounded-xl tw-bg-[deepskyblue] tw-px-6 tw-py-3 tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-[#00a6e6]"
      >
        <span>{t("campaign.page.explore_other_campaigns") || "Explore other campaigns"}</span>
        <ChevronRight className="tw-h-5 tw-w-5" />
      </motion.a>
    </motion.div>
  );
};

// Campaigns that must render in a fixed language regardless of user preference.
// The axios interceptor reads `i18n.language` for the `Language` header, so switching
// here also ensures BE returns trip names / summaries translated to the same locale.
// Currently empty — aurora dropped its zh-Hant-TW lock for the Aurora Promise
// campaign (Jul 2026), which supports English alongside Chinese.
const CAMPAIGN_FORCED_LANGUAGES = {};

const Campaign = () => {
  const { t, i18n } = useTranslation();
  const { language, community, currency, loading, handleChangeLocale } = useUserLocale();
  const [promotion, setPromotion] = useGlobalState("promotion");

  const isMobile = useTabletOrMobile();
  const isDesktop = useIsDesktop();

  const { campaignSlug } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [rawTrips, setRawTrips] = useState([]);
  const [trips, setTrips] = useState([]);
  const [isCampaignLoading, setIsCampaignLoading] = useState(true);
  const [isTripsLoading, setIsTripsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [user] = useGlobalState("user");
  const updateWaitlist = useUpdateWaitlist();

  // Add state for modals at the root level
  const [activeWaitlistTrip, setActiveWaitlistTrip] = useState(null);

  const { isAuthenticated, isLoading: userLoading } = useIsAuthenticated();
  const [error, setError] = useState(null);
  const [cardHeights, setCardHeights] = useState({});

  const [campaignType, setCampaignType] = useState("promocode"); // waitlist, promocode, bundle

  const [openLuckyDrawModal, setOpenLuckyDrawModal] = useState(false);
  const [luckyDrawLoading, setLuckyDrawLoading] = useState(true);
  const [joinedCampaign, setJoinedCampaign] = useState(false);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Check if user has already joined the campaign
  useEffect(() => {
    if (campaign?.id && user?.id) {
      hasJoinedCampaign(campaign.id, user.id).then((res) => {
        setJoinedCampaign(res.data.joined);
      });
    }
  }, [campaign, user?.id]);

  // Handler to join campaign
  const handleJoinCampaign = async () => {
    try {
      setLuckyDrawLoading(true); // Start loading
      setOpenLuckyDrawModal(true);

      // Wait for the API call to complete
      await joinCampaign(campaign.id);
      
      // Only stop loading after API succeeds
      setJoinedCampaign(true);
      setLuckyDrawLoading(false);
    } catch (error) {
      console.error("Error in handleJoinCampaign:", error);
      setLuckyDrawLoading(false); // Stop loading on error
      showToast("Failed to join campaign. Please try again.", "error");
    }
  };

  // Helper function to add earlyAccessTiers to campaign data
  const addEarlyAccessTiers = (campaignData) => {
    // If campaign doesn't have launch_time or launch_method, return original data with default tier
    if (!campaignData.launch_time || campaignData.launch_method !== "early_access") {
      return {
        ...campaignData,
        earlyAccessTiers: [
          {
            requiredReferrals: 2,
            startTime: campaignData.publicSaleStartTime,
            perks: [
              "Early access to booking",
              // `${
              //   campaignData.percentage_or_absolute === "percentage"
              //     ? `${campaignData.discount}% off`
              //     : `$${campaignData.discount} off`
              // }`,
            ],
            achieved: campaignData.userReferrals >= 2,
          },
        ],
      };
    }

    const launchTime = dayjs(campaignData.launch_time);

    // Calculate early access time - 2 hours before launch
    const earlyAccessTime = launchTime.subtract(2, "hours");

    return {
      ...campaignData,
      // Add publicSaleStartTime for backward compatibility
      publicSaleStartTime: campaignData.launch_time,
      earlyAccessTiers: [
        {
          requiredReferrals: 2, // Only one tier that requires 3 referrals
          startTime: earlyAccessTime.toISOString(),
          perks: [
            "Early access 2 hours before public launch",
            // `${
            //   campaignData.percentage_or_absolute === "percentage"
            //     ? `${campaignData.discount}% off`
            //     : `$${campaignData.discount} off`
            // }`,
          ],
          achieved: campaignData.userReferrals >= 2,
        },
      ],
    };
  };

  const [projectView, setProjectView] = useState(true);

  useEffect(() => {
    const processTripsData = (incomingTrips = []) => {
      if (!incomingTrips || incomingTrips.length === 0) {
        return [];
      }

      if (!projectView) {
        return incomingTrips.map((trip) => ({ ...trip }));
      }

      const projectIdToTrip = new Map();
      const projectIdToDates = new Map();

      incomingTrips.forEach((originalTrip) => {
        const trip = { ...originalTrip };
        const formattedDate = dateRangeFormattingShortYear(trip.startDate, trip.endDate);

        const existingDates = projectIdToDates.get(trip.project_id) || [];
        projectIdToDates.set(trip.project_id, [...existingDates, formattedDate]);

        if (!projectIdToTrip.has(trip.project_id)) {
          projectIdToTrip.set(trip.project_id, trip);
        }
      });

      return Array.from(projectIdToTrip.entries()).map(([projectId, trip]) => ({
        ...trip,
        all_dates: projectIdToDates.get(projectId),
      }));
    };

    setTrips(processTripsData(rawTrips));
  }, [projectView, rawTrips]);

  // Apply forced language (e.g. aurora → zh-Hant-TW) before campaign data fetches run.
  // Triggers a re-render via useTranslation so the fetch effects below re-fire with the
  // correct `Language` header.
  useEffect(() => {
    const forcedLanguage = CAMPAIGN_FORCED_LANGUAGES[campaignSlug];
    if (forcedLanguage && i18n.language !== forcedLanguage) {
      i18n.changeLanguage(forcedLanguage);
    }
  }, [campaignSlug, i18n]);

  useEffect(() => {
    if (!campaignSlug) {
      return;
    }

    // Wait for forced language to take effect so the request sends the right Language header.
    const forcedLanguage = CAMPAIGN_FORCED_LANGUAGES[campaignSlug];
    if (forcedLanguage && i18n.language !== forcedLanguage) {
      return;
    }

    const fetchCampaignOverview = async () => {
      try {
        setIsCampaignLoading(true);
        const response = await getCampaignOverviewBySlug(campaignSlug);
        const campaignData = response.data?.campaign;
        if (!campaignData) {
          setError("Campaign not found");
          return;
        }

        const campaignWithTiers = addEarlyAccessTiers(campaignData);
        setCampaign(campaignWithTiers);
        setError(null);
      } catch (err) {
        console.error("Error loading campaign:", err);
        setError("Campaign not found");
      } finally {
        setIsCampaignLoading(false);
      }
    };

    fetchCampaignOverview();
  }, [campaignSlug, i18n.language]);

  useEffect(() => {
    if (!campaignSlug) {
      return;
    }

    const forcedLanguage = CAMPAIGN_FORCED_LANGUAGES[campaignSlug];
    if (forcedLanguage && i18n.language !== forcedLanguage) {
      return;
    }

    const fetchCampaignTrips = async () => {
      try {
        setIsTripsLoading(true);
        const response = await getCampaignTripsBySlug(campaignSlug);
        const tripsData = response.data?.trips || [];
        setRawTrips(tripsData);
      } catch (err) {
        console.error("Error loading campaign trips:", err);
        setRawTrips([]);
        showToast(
          err.response?.data?.error || "Unable to load trips. Please try again later.",
          "error"
        );
      } finally {
        setIsTripsLoading(false);
      }
    };

    fetchCampaignTrips();
  }, [campaignSlug, i18n.language]);

  // Add this useEffect to calculate and set uniform card heights
  useEffect(() => {
    if (trips.length > 0 && !isTripsLoading) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        const cardElements = document.querySelectorAll(".trip-card");
        if (cardElements.length > 0) {
          // Find the tallest card
          let maxHeight = 0;
          cardElements.forEach((card) => {
            const height = card.offsetHeight;
            if (height > maxHeight) {
              maxHeight = height;
            }
          });

          // Set the height for all cards
          if (maxHeight > 0) {
            const newHeights = {};
            trips.forEach((trip) => {
              newHeights[trip.id] = `${maxHeight}px`;
            });
            setCardHeights(newHeights);
          }
        }
      }, 500);
    } else if (trips.length === 0) {
      setCardHeights({});
    }
  }, [trips, isTripsLoading]);

  const copyReferralLink = async (code) => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/welcome-discount?code=${code}&campaign=${campaign.slug}`
      );
      showToast(t("campaign.referral.copy_success"), "success");
    } catch (error) {
      console.error("Error copying text:", error);
      showToast(t("campaign.referral.copy_error"), "error");
    }
  };

  const handleJoinWaitlist = async (trip) => {
    if (!isAuthenticated) {
      window.location = getLoginRedirectUrl();
      return;
    }

    // Check if campaign is past launch time
    if (!isPreLaunchCampaign(campaign)) {
      showToast("This campaign has already launched. Waitlist is no longer available.", "info");
      return;
    }

    try {
      // Use the existing waitlist functionality with trip_id
      await updateWaitlist(
        trip.id, // Using trip_id now
        trip.project_id, // Still passing project_id for reference
        true, // notify
        false, // soldOut
        true // waitlist
      );

      showToast(
        "Successfully joined the waitlist! We'll notify you when early access begins.",
        "success"
      );

      // Refresh campaign data to update waitlist counts
      setIsTripsLoading(true);
      const [campaignResponse, tripsResponse] = await Promise.all([
        getCampaignOverviewBySlug(campaignSlug),
        getCampaignTripsBySlug(campaignSlug),
      ]);

      const campaignData = campaignResponse.data?.campaign;
      if (campaignData) {
        const campaignWithTiers = addEarlyAccessTiers(campaignData);
        setCampaign(campaignWithTiers);
      }

      const updatedTrips = tripsResponse.data?.trips || [];
      setRawTrips(updatedTrips);

      const refreshedActiveTrip =
        updatedTrips.find((updatedTrip) => updatedTrip.id === trip.id) || null;
      setActiveWaitlistTrip(refreshedActiveTrip);
    } catch (error) {
      console.error("Error joining waitlist:", error);
      showToast(
        error.response?.data?.error || "Failed to join waitlist. Please try again.",
        "error"
      );
    } finally {
      setIsTripsLoading(false);
    }
  };

  // Add handlers for showing modals
  const handleShowWaitlistModal = (trip) => {
    // Check if campaign is past launch time
    if (!isPreLaunchCampaign(campaign)) {
      showToast("This campaign has already launched. Waitlist is no longer available.", "info");
      return;
    }

    setActiveWaitlistTrip(trip);
  };

  if (error) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50">
        <div className="tw-p-8 tw-text-center">
          <h1 className="tw-mb-4 tw-text-3xl tw-font-bold tw-text-gray-900">
            {t("campaign.page.error_title")}
          </h1>
          <p className="tw-mb-8 tw-text-gray-600">{error}</p>
          <a
            href="/"
            className="tw-rounded-xl tw-bg-[deepskyblue] tw-px-6 tw-py-3 tw-text-white tw-transition-colors hover:tw-bg-[#00a6e6]"
          >
            {t("campaign.page.back_to_home")}
          </a>
        </div>
      </div>
    );
  }

  if (isCampaignLoading || !campaign) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
          <Loader className="tw-h-8 tw-w-8 tw-animate-spin tw-text-[deepskyblue]" />
        </div>
      </div>
    );
  }

  // Get campaign-specific configuration
  const campaignSettings = getCampaignConfig(campaign.slug);
  const isAurora = campaign?.slug === "aurora";

  return (
    <div
      className={
        isAurora
          ? "tw-min-h-screen"
          : "tw-min-h-screen tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50"
      }
      style={
        isAurora
          ? { backgroundColor: AURORA_THEME.bg.primary, color: AURORA_THEME.text.primary }
          : undefined
      }
    >
      <LuckyDrawModal
        open={openLuckyDrawModal}
        setOpen={setOpenLuckyDrawModal}
        promoPrizeAmount={"88"}
        promoCode="UPTOSNOWGOOD88"
        luckyDrawLoading={luckyDrawLoading}
        campaignSlug={campaign?.slug}
        voucherAnnouncementDate={
          campaign?.voucher_validity_start_date 
            ? convertUTCToLocalDateTimeString(campaign.voucher_validity_start_date)
            : "17 Mar, 12:00 AM"
        }
        campaign={campaign}
        joinedCampaign={joinedCampaign}
      />
      <StickyHeader />
      {campaign && <SaleTimer campaign={campaign} />}

        {/* Campaign Title Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="tw-absolute tw-left-0 tw-right-0 tw-top-1/4 tw-z-10 tw-mx-auto tw-max-w-3xl -tw-translate-y-1/2 tw-transform tw-px-4"
        >
          <h1 className="tw-text-center tw-text-[22px] tw-font-extrabold tw-text-white md:tw-text-5xl">
            {campaign?.slug === "eu-go-i-go"
              ? t("landing.destination.europe.main_text")
              : campaign?.slug === "aurora"
              ? t("campaign.aurora.hero.title")
              : campaign?.title}
          </h1>
          <p className="tw-text-center tw-text-[12px] tw-font-semibold tw-text-white md:tw-text-2xl">
            {campaign?.slug === "eu-go-i-go"
              ? t("landing.destination.europe.sub_text")
              : campaign?.slug === "aurora"
              ? t("campaign.aurora.hero.subtitle")
              : campaign?.description}
          </p>
          {/* Remove countdown timer for eu-go-i-go and aurora campaigns */}
          {campaign &&
            isPreLaunchCampaign(campaign) &&
            campaign?.slug !== "eu-go-i-go" &&
            campaign?.slug !== "aurora" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-gap-2"
            >
              <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-black/30 tw-px-4 tw-py-2 tw-backdrop-blur-sm">
                <Clock className="tw-h-4 tw-w-4 tw-text-white" />
                <span className="tw-text-sm tw-font-medium tw-text-white md:tw-text-base">
                  {campaign.launch_time
                    ? `${t(
                        "campaign.page.booking_opens"
                      )} ${convertUTCToLocalDateTimeString(campaign.launch_time)}`
                    : campaign.publicSaleStartTime
                      ? `${t("campaign.page.booking_opens")} ${convertUTCToLocalDateTimeString(
                          campaign.publicSaleStartTime
                        )}`
                      : "Coming soon"}
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>

      {/* Hero Video/Image - Responsive sizing and overlay */}
      <div className="tw-relative tw-h-[calc(100vh)] tw-w-full">
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-black/10 tw-to-black/10"></div>

        {campaignSettings.heroVideo ? (
          <>
            {isDesktop && (
              <AutoPlaySilentVideo
                className="tw-h-full tw-w-full tw-object-cover tw-object-center md:tw-block"
                video={campaignSettings.heroVideo.desktop}
              />
            )}

            {isMobile && (
              <AutoPlaySilentVideo
                className="tw-h-full tw-w-full tw-object-cover tw-object-center"
                video={campaignSettings.heroVideo.mobile}
              />
            )}
          </>
        ) : (
          // Placeholder hero when no video is configured yet (e.g. aurora pre-launch).
          // Deep night-sky gradient evokes aurora skies without committing to final art.
          <div
            aria-hidden="true"
            className="tw-h-full tw-w-full"
            style={{
              background: isAurora
                ? AURORA_THEME.gradients.hero
                : "linear-gradient(180deg, #312e81 0%, #581c87 50%, #0f172a 100%)",
            }}
          />
        )}

        {/* Campaign Hero Logo Overlay */}
        {campaignSettings.heroLogo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center"
          >
            <motion.img
              src={campaignSettings.heroLogo}
              alt={campaign?.title || "Campaign logo"}
              animate={{
                y: [0, -8, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="tw-w-[280px] tw-drop-shadow-2xl md:tw-w-[420px]"
            />
          </motion.div>
        )}
      </div>

      {isAurora ? (
        <>
          {/* Aurora Promise dark-mode body. Order: Story → Promises → Spotlight (single inventory section) → Reasons → FAQ. */}
          <AuroraStory />

          <AuroraPromises />

          <div id={AURORA_ANCHORS.trips}>
            {isTripsLoading ? (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                <motion.div
                  animate={{ rotate: 360, scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Loader
                    className="tw-h-8 tw-w-8"
                    style={{ color: AURORA_THEME.accent.primary }}
                  />
                </motion.div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="tw-mt-4"
                  style={{ color: AURORA_THEME.text.secondary }}
                >
                  {t("campaign.page.loading")}
                </motion.p>
              </div>
            ) : (
              <AuroraDestinationSpotlight trips={trips} />
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="tw-mt-12"
          >
            <AuroraReasonsGrid
              title={t("campaign.aurora.reasons.title")}
              description={t("campaign.aurora.reasons.description")}
              reasons={auroraDestinationReasons}
            />
          </motion.div>

          <motion.div
            id={AURORA_ANCHORS.faqs}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="tw-mx-auto tw-mt-16 tw-max-w-4xl tw-px-4 tw-pb-16 sm:tw-px-6 lg:tw-px-8"
          >
            <AuroraFAQs customFaqs={auroraFaqs} />
          </motion.div>
        </>
      ) : (
      <div className="tw-pb-16 tw-pt-16">
        {/* <div className="tw-max-w-4xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="tw-mb-8 tw-text-center"
          >
            <h1 className="tw-text-4xl tw-font-bold tw-bg-gradient-to-r tw-from-[deepskyblue] tw-to-blue-600 tw-bg-clip-text tw-text-transparent tw-mb-4">
              {tPromotions[campaign.id]?.name || campaign?.title}
            </h1>
            <p className="tw-text-gray-600 tw-text-lg">
              {tPromotions[campaign.id]?.description || campaign?.description}
            </p>
          </motion.div>

          <div className="tw-space-y-8">
            <CampaignExplainer campaign={campaign} community={community} />
          </div>
        </div> */}

        {campaignType === "bundle" ? (
          <div className="tw-mx-auto tw-max-w-4xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="tw-mb-8 tw-text-center"
            >
              <h1 className="tw-mb-4 tw-bg-gradient-to-r tw-from-[deepskyblue] tw-to-blue-600 tw-bg-clip-text tw-text-4xl tw-font-bold tw-text-transparent">
                {t("campaign.explainer.how_it_works")}
              </h1>
              {campaignSettings.howItWorks.steps.map((step, index) => (
                <p key={index} className="tw-text-lg tw-font-bold tw-text-gray-600">
                  {index + 1}. {step}
                </p>
              ))}
            </motion.div>

            <div className="tw-space-y-8">
              <BundlePass campaign={campaign} user_id={user?.id} campaignSettings={campaignSettings} />
            </div>
          </div>
        ) : (
          <div className="tw-mx-auto tw-max-w-4xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="tw-mb-8 tw-text-center"
            >
              <h1 className="tw-mb-4 tw-bg-gradient-to-r tw-from-[deepskyblue] tw-to-blue-600 tw-bg-clip-text tw-text-4xl tw-font-bold tw-text-transparent">
                {campaign?.slug === "eu-go-i-go"
                  ? t("campaign.europe.how_it_works_title")
                  : t("campaign.explainer.how_it_works")}
              </h1>
              {campaign?.slug === "eu-go-i-go"
                ? [
                    t("campaign.europe.how_it_works.step_1"),
                    t("campaign.europe.how_it_works.step_2"),
                    t("campaign.europe.how_it_works.step_3"),
                  ].map((step, index) => (
                    <p key={index} className="tw-text-lg tw-font-bold tw-text-gray-600">
                      {index + 1}. {step}
                    </p>
                  ))
                : campaignSettings.howItWorks.steps.map((step, index) => (
                    <p key={index} className="tw-text-lg tw-font-bold tw-text-gray-600">
                      {index + 1}. {step}
                    </p>
                  ))}
            </motion.div>

            {campaign?.slug !== "eu-go-i-go" && (
              <div className="tw-space-y-8">
                <CampaignPromoCodes
                  campaign={campaign}
                  user_id={user?.id}
                  setOpenLuckyDrawModal={setOpenLuckyDrawModal}
                  setLuckyDrawLoading={setLuckyDrawLoading}
                  campaignSettings={campaignSettings}
                />
              </div>
            )}
          </div>
        )}

        {/* Europe Campaign Specific Full-Width Sections */}
        {campaign?.slug === "eu-go-i-go" && (
          <>
            {/* Europe Campaign Perks - Centered */}
            <div className="tw-mx-auto tw-max-w-4xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
              <EuropeCampaignPerks
                setOpenLuckyDrawModal={setOpenLuckyDrawModal}
                setLuckyDrawLoading={setLuckyDrawLoading}
                campaign={campaign}
                onJoinCampaign={handleJoinCampaign}
                joinedCampaign={joinedCampaign}
                isAuthenticated={isAuthenticated}
                community={community}
              />
            </div>

            {/* Why Choose Europe Section - Full Width */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <WhyChooseUs
                title={t("landing.destination.europe.why_destination_title")}
                description={t("landing.destination.europe.why_destination_description")}
                reasons={europeDestinationReasons}
                mobileVariant="horizontal"
                removeCta={true}
              />
            </motion.div>
          </>
        )}

        {/* Projects Grid - Full width */}
        <div
          id="available-trips"
          className="tw-mx-auto tw-mt-16 tw-max-w-7xl tw-px-4 sm:tw-px-6 lg:tw-px-8"
        >
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tw-mb-8 tw-text-center tw-text-2xl tw-font-bold tw-text-gray-900"
          >
            {campaignType === "bundle"
              ? t("campaign.page.available_destinations_bundle")
              : t("campaign.page.available_destinations")}
          </motion.h2>

          {isTripsLoading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Loader className="tw-h-8 tw-w-8 tw-text-[deepskyblue]" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mt-4 tw-text-gray-500"
              >
                {t("campaign.page.loading")}
              </motion.p>
            </div>
          ) : trips.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tw-grid tw-transform-gpu tw-gap-6 md:tw-grid-cols-2 lg:tw-grid-cols-3"
            >
              {trips.map((trip, index) => (
                <motion.div
                  key={trip.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{
                    duration: 0.3,
                    ease: "easeOut",
                    delay: Math.min(index * 0.1, 0.8),
                  }}
                  className="trip-card"
                >
                  <TripCard
                    trip={trip}
                    onShowToast={showToast}
                    onJoinWaitlist={() => handleJoinWaitlist(trip)}
                    cardHeight={cardHeights[trip.id] || "auto"}
                    onShowWaitlistModal={handleShowWaitlistModal}
                    campaign={campaign}
                    promotion={promotion}
                    projectView={projectView}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <EmptyTripsState campaign={campaign} />
          )}
        </div>
        
        {/* Europe Campaign FAQs */}
        {campaign?.slug === "eu-go-i-go" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="tw-mx-auto tw-mt-16 tw-max-w-7xl tw-px-4 sm:tw-px-6 lg:tw-px-8"
          >
            <FAQs customFaqs={europeFaqs} />
          </motion.div>
        )}

        {/* Aurora FAQs render inside the dark aurora branch above; this block stays for non-aurora compatibility but is unreachable when slug === "aurora" */}
      </div>
      )}

      {!isAurora && <ScrollIndicator />}

      <AnimatePresence mode="wait">
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        <WaitlistModal
          key="waitlist-modal"
          waitlisters={activeWaitlistTrip?.waitlisters}
          totalCount={activeWaitlistTrip?.waitlistCount}
          tripTitle={activeWaitlistTrip?.title}
          onClose={() => setActiveWaitlistTrip(null)}
          onShowToast={showToast}
          trip={activeWaitlistTrip}
          onJoinWaitlist={() => handleJoinWaitlist(activeWaitlistTrip)}
          open={activeWaitlistTrip && isPreLaunchCampaign(campaign)}
        />
      </AnimatePresence>

      {isAurora ? <AuroraDarkFooter /> : <Footer />}
    </div>
  );
};

export default Campaign;

const ReferralProgress = ({ campaign, onCopyLink }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user } = useIsAuthenticated();
  const [isSharePopoverOpen, setIsSharePopoverOpen] = useState(false);
  const sharePopoverRef = useRef(null);
  const shareButtonRef = useRef(null);

  // Handle clicks outside the popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSharePopoverOpen &&
        sharePopoverRef.current &&
        !sharePopoverRef.current.contains(event.target) &&
        shareButtonRef.current &&
        !shareButtonRef.current.contains(event.target)
      ) {
        setIsSharePopoverOpen(false);
      }
    };

    if (isSharePopoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSharePopoverOpen]);

  // Handle share action and close popover
  const handleShare = async (shareMethod) => {
    if (shareMethod === "navigator" && navigator.share) {
      try {
        await navigator.share({
          title: t("campaign.referral.share_title"),
          text: t("campaign.referral.share_text"),
          url: `${window.location.origin}/welcome-discount?code=${user.id}&campaign=${campaign.slug}`,
        });
      } catch (err) {
        console.log(err);
      }
    } else if (shareMethod === "copy") {
      onCopyLink(user.id);
    }

    // Close popover after sharing
    setIsSharePopoverOpen(false);
  };

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="tw-overflow-hidden tw-rounded-3xl tw-bg-white tw-shadow-xl"
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-p-12">
          <Loader className="tw-h-8 tw-w-8 tw-animate-spin tw-text-[deepskyblue]" />
        </div>
      </motion.div>
    );
  }

  if (!isAuthenticated) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="tw-overflow-hidden tw-rounded-3xl tw-bg-white tw-shadow-xl"
      >
        <div className="tw-relative tw-bg-gradient-to-r tw-from-emerald-400 tw-to-sky-500 tw-px-8 tw-py-10">
          <div className="tw-bg-grid-white/[0.2] tw-absolute tw-inset-0 tw-opacity-20" />
          <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-text-center">
            <h2 className="tw-mb-6 tw-text-3xl tw-font-bold tw-text-white">
              {t("campaign.referral.signin_title")}
            </h2>
            <p className="tw-mb-8 tw-text-white/90">{t("campaign.referral.signin_subtitle")}</p>
            <motion.a
              href={`/login?reroute=${window.location.pathname}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="tw-rounded-xl tw-bg-white tw-px-8 tw-py-3 tw-font-medium tw-text-[deepskyblue] tw-shadow-lg tw-transition-shadow hover:tw-shadow-xl"
            >
              {t("campaign.referral.signin_button")}
            </motion.a>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="tw-overflow-hidden tw-rounded-3xl tw-bg-white tw-shadow-xl"
    >
      {/* Header Section with Gradient Background */}
      <div className="tw-relative tw-bg-gradient-to-r tw-from-emerald-400 tw-to-sky-500 tw-px-8 tw-py-10">
        <div className="tw-bg-grid-white/[0.2] tw-absolute tw-inset-0 tw-opacity-20" />
        <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring" }}
            className="tw-mb-6 tw-rounded-full tw-bg-white/20 tw-px-6 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-backdrop-blur-sm"
          >
            {t("campaign.referral_progress.your_progress")}
          </motion.div>
          <h2 className="tw-mb-6 tw-text-3xl tw-font-bold tw-text-white">
            {campaign.earlyAccessTiers && campaign.earlyAccessTiers.find((tier) => !tier.achieved)
              ? `${t("campaign.referral_progress.more_friends", {
                  count: campaign.earlyAccessTiers[0].requiredReferrals - campaign.userReferrals,
                })}`
              : t("campaign.referral_progress.early_access_unlocked")}
          </h2>

          {/* Progress Bar */}
          <div className="tw-w-full tw-max-w-md">
            <div className="tw-relative tw-h-4 tw-overflow-hidden tw-rounded-full tw-bg-white/20 tw-shadow-inner">
              <motion.div
                initial={{ width: 0 }}
                animate={{
                  width: `${Math.min(
                    (campaign.userReferrals / campaign.earlyAccessTiers[0].requiredReferrals) * 100,
                    100
                  )}%`,
                }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="tw-absolute tw-h-full tw-rounded-full tw-bg-white tw-shadow-lg"
              />
            </div>
            <div className="tw-mt-3 tw-flex tw-justify-between tw-text-white/90">
              <span className="tw-text-sm tw-font-medium">
                {t("campaign.referral_progress.normal_access")}
              </span>
              <span className="tw-text-sm tw-font-medium">
                {t("campaign.referral_progress.early_access")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="tw-px-8 tw-py-10">
        {/* Referral Link - Enhanced Modern Design with Share Popover */}
        <div className="tw-relative tw-mb-8">
          <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-space-x-2">
              <LinkIcon className="tw-h-4 tw-w-4 tw-text-sky-500" />
              <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                {t("campaign.referral_progress.your_referral_link")}
              </span>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="tw-flex tw-items-center tw-rounded-full tw-bg-sky-50 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-sky-600"
            >
              <Gift className="tw-mr-1 tw-h-3.5 tw-w-3.5" />
              {t("campaign.referral_progress.earn_rewards")}
            </motion.div>
          </div>

          {/* Restructured to fix overflow issue */}
          <div className="tw-flex tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white">
            {/* Referral Link */}
            <div className="tw-flex-1 tw-overflow-hidden">
              <div
                onClick={() => onCopyLink(user.id)}
                className="tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-px-4 tw-py-3.5 tw-transition-colors hover:tw-bg-gray-50"
              >
                <div className="tw-mr-3 tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-sky-100">
                  <LinkIcon className="tw-h-4 tw-w-4 tw-text-sky-500" />
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-mb-0.5 tw-text-xs tw-text-gray-500">
                    {t("campaign.referral_progress.click_to_copy")}
                  </div>
                  <div className="tw-truncate tw-text-sm tw-font-medium tw-text-gray-700">
                    {`${window.location.origin}/welcome-discount?code=${user.id}&campaign=${campaign.slug}`}
                  </div>
                </div>
              </div>
            </div>

            {/* Share Button - Now outside the overflow hidden container */}
            <button
              ref={shareButtonRef}
              onClick={() => onCopyLink(user.id)}
              className="tw-flex tw-items-center tw-justify-center tw-rounded-r-xl tw-border-l tw-border-gray-200 tw-bg-gray-50 tw-px-4 tw-transition-colors hover:tw-bg-sky-50"
            >
              <div className="tw-flex tw-flex-col tw-items-center">
                <Share2 className="tw-h-5 tw-w-5 tw-text-sky-500" />
                <span className="tw-mt-1 tw-text-xs tw-font-medium tw-text-gray-600">
                  {t("campaign.referral.share")}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Referred Users Section */}
        {campaign.referredUsers && campaign.referredUsers.length > 0 && (
          <div className="tw-mt-10">
            <div className="tw-mb-6 tw-flex tw-items-center tw-space-x-2">
              <Users className="tw-h-5 tw-w-5 tw-text-sky-500" />
              <span className="tw-font-semibold tw-text-gray-900">
                {t("campaign.referral_progress.successfully_referred")}
              </span>
            </div>

            <div className="tw-space-y-3">
              {campaign.referredUsers.map((user, index) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="tw-flex tw-items-center tw-rounded-xl tw-bg-gray-50 tw-p-3 tw-transition-colors hover:tw-bg-gray-100"
                >
                  <ImageResizable
                    size="xs"
                    src={user.avatar_url}
                    alt={user.name}
                    className="tw-mr-3 tw-h-10 tw-w-10 tw-rounded-full tw-border-2 tw-border-white tw-object-cover"
                  />
                  <div className="tw-flex-1">
                    <div className="tw-font-medium tw-text-gray-900">{user.name}</div>
                    <div className="tw-text-xs tw-text-gray-500">
                      {t("campaign.referral_progress.referral_date")}{" "}
                      {new Date(user.joinedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="tw-rounded-full tw-bg-emerald-100 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-emerald-600">
                    {t("campaign.referral_progress.referred_by_you")}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {campaign.referredUsers && campaign.referredUsers.length === 0 && (
          <div className="tw-mt-10">
            <div className="tw-mb-6 tw-flex tw-items-center tw-space-x-2">
              <Users className="tw-h-5 tw-w-5 tw-text-sky-500" />
              <span className="tw-font-semibold tw-text-gray-900">
                {t("campaign.referral_progress.successfully_referred")}
              </span>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tw-flex tw-flex-col tw-items-center tw-rounded-xl tw-bg-gray-50 tw-p-6 tw-text-center"
            >
              <div className="tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100">
                <Users className="tw-h-8 tw-w-8 tw-text-gray-400" />
              </div>
              <h4 className="tw-mb-2 tw-font-medium tw-text-gray-700">
                {t("campaign.referral_progress.no_friends_joined")}
              </h4>
              <p className="tw-mb-4 tw-text-sm tw-text-gray-500">
                {t("campaign.referral_progress.invite_friends_for_early_access")}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onCopyLink(user.id)}
                className="tw-flex tw-items-center tw-rounded-lg tw-bg-sky-500 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white"
              >
                <Copy className="tw-mr-2 tw-h-4 tw-w-4" />
                {t("campaign.referral.copy_link")}
              </motion.button>
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
